import { CTA_TYPE_OPTIONS, MLP_NAVBAR_LOGO } from '@/common/constants';
import { CTAProps } from '@/components/CTA';
import React from 'react';
import { useIntl } from 'react-intl';
import { DesktopNavbar } from './DesktopNavbar';
import { MobileNavbar } from './MobileNavbar';
import styles from './Navbar.module.scss';
import { NavbarProps } from './types';

const CREATE_EVENTS_OVERVIEW_URL = 'https://www.eventbrite.com/organizer/overview/';
const CREATE_EVENTS_OVERVIEW_PATH = '/organizer/overview/';
const CREATE_EVENTS_DIRECT_SIGNUP_PATH = '/signin/signup';
const CREATE_EVENTS_DIRECT_SIGNUP_QUERY = '?referrer=/&utm_source=header_create_events';
const TREATMENT_CTA_TRACKING_IDS = {
  createEvents: 'mlp-webapp-navbar-item-create-events',
  contactSales: 'mlp-webapp-navbar-item-contact-sales',
  getStarted: 'mlp-webapp-navbar-item-get-started',
} as const;
const TREATMENT_CTA_MESSAGES = {
  createEvents: {
    id: 'headerCreatorCta.createEvents',
    defaultMessage: 'Create Events',
    description: 'Navbar CTA for starting event creation',
  },
};
const DEFAULT_TREATMENT_CTA_LABELS = {
  createEvents: 'Create Events',
};

type TreatmentCtaLabels = typeof DEFAULT_TREATMENT_CTA_LABELS;

const getCreateEventsUrl = (
  creatorVariant: NavbarProps['creatorVariant'],
  getStartedUrl: string,
) => {
  const url = new URL(getStartedUrl, CREATE_EVENTS_OVERVIEW_URL);
  const localePrefix = url.pathname.match(/^\/[a-z]{2}_[A-Z]{2}(?=\/)/)?.[0] ?? '';
  const isOrangeTreatment = creatorVariant === 'orange-direct-signup';

  url.pathname = `${localePrefix}${
    isOrangeTreatment ? CREATE_EVENTS_DIRECT_SIGNUP_PATH : CREATE_EVENTS_OVERVIEW_PATH
  }`;
  url.search = isOrangeTreatment ? CREATE_EVENTS_DIRECT_SIGNUP_QUERY : '';
  url.hash = '';

  return url.toString();
};

export const getNavbarCtas = (
  ctas: CTAProps[] | undefined,
  creatorVariant: NavbarProps['creatorVariant'],
  isUserAuthenticated: boolean | undefined,
  treatmentLabels: TreatmentCtaLabels = DEFAULT_TREATMENT_CTA_LABELS,
): CTAProps[] | undefined => {
  if (
    isUserAuthenticated !== false ||
    !ctas ||
    ctas.length < 2 ||
    (creatorVariant !== 'restore-2025-layout' && creatorVariant !== 'orange-direct-signup')
  ) {
    return ctas;
  }

  const [contactSalesCta, getStartedCta, ...remainingCtas] = ctas;
  const isOrangeTreatment = creatorVariant === 'orange-direct-signup';

  return [
    {
      label: treatmentLabels.createEvents,
      link: getCreateEventsUrl(creatorVariant, getStartedCta.link),
      type: CTA_TYPE_OPTIONS.PRIMARY,
      dataHeapId: TREATMENT_CTA_TRACKING_IDS.createEvents,
      variant: isOrangeTreatment ? 'orange-text' : 'plain',
    },
    {
      ...contactSalesCta,
      dataHeapId: TREATMENT_CTA_TRACKING_IDS.contactSales,
    },
    {
      ...getStartedCta,
      dataHeapId: TREATMENT_CTA_TRACKING_IDS.getStarted,
      ...(isOrangeTreatment && { variant: 'orange-filled' as const }),
    },
    ...remainingCtas,
  ];
};

export const Navbar: React.FC<NavbarProps> = ({
  title = MLP_NAVBAR_LOGO,
  titleLink = '/',
  navItems,
  ctas,
  creatorVariant,
  isUserAuthenticated,
}) => {
  const { formatMessage } = useIntl();
  const navbarCtas = getNavbarCtas(ctas, creatorVariant, isUserAuthenticated, {
    createEvents: formatMessage(TREATMENT_CTA_MESSAGES.createEvents),
  });

  return (
    <>
      <DesktopNavbar
        title={title}
        titleLink={titleLink}
        navItems={navItems}
        ctas={navbarCtas}
        className={styles.navbar_desktop}
      />
      <MobileNavbar
        title={title}
        titleLink={titleLink}
        navItems={navItems}
        ctas={navbarCtas}
        className={styles.navbar_mobile}
      />
    </>
  );
};
